import React, { useCallback, useEffect, useMemo, useState } from "react";

function fmt(n) { return Number(n || 0).toFixed(2); }
function fmtGr(n) { return Number(n || 0).toFixed(3); }
function today() { return new Date().toISOString().slice(0, 10); }
function oneMonthAgo() {
  const d = new Date(); d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const p = (x) => String(x).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const ZONES = [
  { id: "dispensario", label: "Dispensario Cannábico" },
  { id: "barra", label: "Bebida / comida social" },
];

const DETAIL_TABS = [
  { id: "aportaciones", label: "Aportaciones" },
  { id: "produccion", label: "Producción" },
  { id: "devolucion", label: "Devolución" },
];

// Columnas de la tabla. `numeric` => orden por número; si no, alfabético (Genética).
const COLUMNS = [
  { key: "name", label: "Genética", numeric: false },
  { key: "gr_aportados", label: "GR aportados", numeric: true },
  { key: "crd_aportados", label: "CRD aportados", numeric: true },
  { key: "crd_gr_aportacion", label: "CRD/GR", numeric: true },
  { key: "gr_produccion", label: "GR producción", numeric: true },
  { key: "crd_produccion", label: "CRD producción", numeric: true },
  { key: "rendimiento", label: "Rendimiento", numeric: true },
];

/** Cantidad aportada como en danimateu: gr para peso, "N und" para fijos. */
function qtyLabel(row) {
  if (row.unit_type === "fixed") {
    const und = row.und_aportados != null ? row.und_aportados : 0;
    return (
      <span>
        {fmtGr(row.gr_aportados)} gr.
        {und > 0 && <span className="block text-xs italic text-gray-500">{und} und</span>}
      </span>
    );
  }
  return <span>{fmtGr(row.gr_aportados)} gr.</span>;
}

export default function ContabilidadGeneral({ token, venueID, beforeZones = null }) {
  const [zone, setZone] = useState("dispensario");
  const [fromDate, setFromDate] = useState(oneMonthAgo);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Orden de la tabla (por defecto: más gramos aportados primero)
  const [sortKey, setSortKey] = useState("gr_aportados");
  const [sortDir, setSortDir] = useState("desc");

  const onSort = useCallback((col) => {
    setSortKey((prevKey) => {
      if (prevKey === col.key) {
        setSortDir((d) => (d === "desc" ? "asc" : "desc"));
        return prevKey;
      }
      // Nueva columna: numéricas empiezan en desc (más → menos); texto en asc.
      setSortDir(col.numeric ? "desc" : "asc");
      return col.key;
    });
  }, []);

  // Modal de detalle por genética
  const [detailRow, setDetailRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("aportaciones");
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token || !venueID) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(
        `/v1/reports/contabilidad-genetica?venue_id=${venueID}&zone=${zone}&from_date=${fromDate}&to_date=${toDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Error al cargar la contabilidad");
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, venueID, zone, fromDate, toDate]);

  // Recarga al montar y al cambiar de zona
  useEffect(() => { fetchData(); }, [zone]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = useCallback(async (row) => {
    setDetailRow(row);
    setDetailTab("aportaciones");
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await fetch(
        `/v1/reports/contabilidad-genetica/${row.product_id}?venue_id=${venueID}&from_date=${fromDate}&to_date=${toDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Error al cargar el detalle");
      setDetail(await res.json());
    } catch {
      setDetail({ aportaciones: [], produccion: [], devolucion: [] });
    } finally {
      setDetailLoading(false);
    }
  }, [token, venueID, fromDate, toDate]);

  const rows = useMemo(() => {
    const base = data?.rows || [];
    const col = COLUMNS.find((c) => c.key === sortKey) || COLUMNS[1];
    const dir = sortDir === "asc" ? 1 : -1;
    return [...base].sort((a, b) => {
      if (col.numeric) {
        return (Number(a[col.key] || 0) - Number(b[col.key] || 0)) * dir;
      }
      return String(a[col.key] || "").localeCompare(String(b[col.key] || ""), "es", { sensitivity: "base" }) * dir;
    });
  }, [data, sortKey, sortDir]);

  return (
    <div className="space-y-4">
      {/* Zona (+ slot opcional a la izquierda, p.ej. el conmutador Gráficas/Balance) */}
      <div className="flex flex-wrap items-center gap-3">
        {beforeZones}
        <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {ZONES.map((z) => (
            <button
              key={z.id}
              onClick={() => setZone(z.id)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                zone === z.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {z.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filtro de fechas + balance */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <label className="text-gray-500 font-medium">Desde</label>
          <input
            type="date"
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-400"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-gray-500 font-medium">hasta</label>
          <input
            type="date"
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-400"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="px-4 py-1.5 bg-lime-500 hover:bg-lime-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
        >
          {loading ? "Cargando…" : "Ver Balance"}
        </button>
        {data && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-2 rounded-lg">
              Flujo contable {fmt(data.flujo_contable)} Crd
            </div>
            <div className="bg-gray-900 text-white text-sm font-bold px-4 py-2 rounded-lg">
              BALANCE TOTAL = {fmt(data.balance_total)} Crd
            </div>
          </div>
        )}
      </div>

      {error && <div className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      {loading && <div className="text-center py-16 text-gray-400 text-sm">Cargando datos…</div>}

      {!loading && data && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {COLUMNS.map((col) => {
                    const active = sortKey === col.key;
                    return (
                      <th key={col.key} className={col.numeric ? "text-right px-4 py-3" : "text-left px-4 py-3"}>
                        <button
                          type="button"
                          onClick={() => onSort(col)}
                          className={`inline-flex items-center gap-1 hover:text-gray-800 transition-colors ${active ? "text-gray-900" : ""}`}
                          title="Ordenar"
                        >
                          <span>{col.label}</span>
                          <span className={active ? "text-lime-600" : "text-gray-300"}>
                            {active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-10 text-gray-400 text-sm">Sin datos en el período</td></tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.product_id}
                      onClick={() => openDetail(row)}
                      className="hover:bg-lime-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-2.5 font-medium text-gray-900">{row.name}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{qtyLabel(row)}</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{fmt(row.crd_aportados)} Crd</td>
                      <td className="px-4 py-2.5 text-right text-gray-500">{fmt(row.crd_gr_aportacion)} Crd/gr.</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{fmtGr(row.gr_produccion)} gr.</td>
                      <td className="px-4 py-2.5 text-right text-gray-700">{fmt(row.crd_produccion)} Crd</td>
                      <td className={`px-4 py-2.5 text-right font-semibold ${row.rendimiento < 0 ? "text-red-500" : "text-gray-900"}`}>
                        {fmt(row.rendimiento)} Crd
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de detalle por genética */}
      {detailRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setDetailRow(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-bold text-gray-800">Genética {detailRow.name}</h2>
              <button
                onClick={() => setDetailRow(null)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                aria-label="Cerrar"
              >×</button>
            </div>

            <div className="flex gap-1 border-b border-gray-200 px-5 pt-3">
              {DETAIL_TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setDetailTab(t.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    detailTab === t.id
                      ? "bg-gray-100 text-gray-900 border-b-2 border-lime-500"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {detailLoading ? (
                <div className="text-center py-12 text-gray-400 text-sm">Cargando…</div>
              ) : detailTab === "produccion" ? (
                <DetailTable
                  cols={["Concepto", "Gramos", "Importe", "Fecha"]}
                  rows={(detail?.produccion || []).map((r) => [
                    r.concepto,
                    `${fmtGr(r.gramos)} gr.`,
                    `${fmt(r.importe)} Crd`,
                    fmtDate(r.fecha),
                  ])}
                  empty="Sin entradas de producción en el período"
                />
              ) : (
                <DetailTable
                  cols={["Socio", "Socio Nº", "Gramos", "Importe", "Fecha"]}
                  rows={((detailTab === "devolucion" ? detail?.devolucion : detail?.aportaciones) || []).map((r) => [
                    r.socio,
                    r.socio_num ?? "—",
                    `${fmtGr(r.gramos)} gr.`,
                    `${fmt(r.importe)} Crd`,
                    fmtDate(r.fecha),
                  ])}
                  empty={detailTab === "devolucion" ? "Sin devoluciones en el período" : "Sin aportaciones en el período"}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailTable({ cols, rows, empty }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {cols.map((c, i) => (
            <th key={c} className={`px-3 py-2 ${i === 0 ? "text-left" : "text-right"}`}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 ? (
          <tr><td colSpan={cols.length} className="text-center py-8 text-gray-400 text-sm">{empty}</td></tr>
        ) : (
          rows.map((r, ri) => (
            <tr key={ri} className="hover:bg-gray-50">
              {r.map((cell, ci) => (
                <td key={ci} className={`px-3 py-2 ${ci === 0 ? "text-left text-gray-700 font-medium" : "text-right text-gray-700"}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
